import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Divider,
  Stack,
  Paper
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CheckIcon,
  Settings as SettingsIcon,
  Storage as DatabaseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';

const Settings = () => {
  const [settings, setSettings] = useState({
    apiUrl: '',
    wooCommerceUrl: '',
    consumerKey: '',
    consumerSecret: ''
  });
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [dbTestResult, setDbTestResult] = useState(null);
  const [dbTesting, setDbTesting] = useState(false);

  useEffect(() => {
    // Load settings from localStorage
    const savedApiUrl = localStorage.getItem('apiUrl') || '';
    const savedWooCommerceUrl = localStorage.getItem('wooCommerceUrl') || '';
    const savedConsumerKey = localStorage.getItem('consumerKey') || '';
    const savedConsumerSecret = localStorage.getItem('consumerSecret') || '';

    setSettings({
      apiUrl: savedApiUrl || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api'),
      wooCommerceUrl: savedWooCommerceUrl,
      consumerKey: savedConsumerKey,
      consumerSecret: savedConsumerSecret
    });
  }, []);

  const handleChange = (field) => (event) => {
    setSettings({
      ...settings,
      [field]: event.target.value
    });
    setSaveSuccess(false);
    setTestResult(null);
  };

  const handleSave = async () => {
    try {
      // Save API URL to localStorage
      localStorage.setItem('apiUrl', settings.apiUrl);
      
      // Update API base URL
      if (window.updateApiBaseUrl) {
        window.updateApiBaseUrl(settings.apiUrl);
      }

      // Save WooCommerce credentials to backend if provided
      if (settings.wooCommerceUrl && settings.consumerKey && settings.consumerSecret) {
        const apiUrl = settings.apiUrl || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
        
        try {
          const response = await axios.post(`${apiUrl}/woocommerce/settings`, {
            wooCommerceUrl: settings.wooCommerceUrl,
            consumerKey: settings.consumerKey,
            consumerSecret: settings.consumerSecret
          });
          
          if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to save credentials');
          }
        } catch (error) {
          // Extract error message from response
          const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message;
          throw new Error(errorMessage);
        }
      }

      // Also save to localStorage as backup
      localStorage.setItem('wooCommerceUrl', settings.wooCommerceUrl);
      localStorage.setItem('consumerKey', settings.consumerKey);
      localStorage.setItem('consumerSecret', settings.consumerSecret);

      setSaveSuccess(true);
      setTestResult(null); // Clear any previous errors
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveSuccess(false);
      setTestResult({
        success: false,
        message: `Fout bij opslaan: ${error.response?.data?.error || error.message || 'Onbekende fout'}`
      });
    }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    setTestResult(null);

    try {
      // Create a test axios instance with the configured API URL
      const finalApiUrl = settings.apiUrl || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const testApi = axios.create({
        baseURL: finalApiUrl,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const response = await testApi.get('/health');
      
      // Build status message from response
      let statusMessage = 'Verbinding succesvol! Backend is bereikbaar.';
      if (response.data.database) {
        statusMessage += ` Database: ${response.data.database.status} - ${response.data.database.message}`;
      }
      
      setTestResult({
        success: true,
        message: statusMessage
      });
    } catch (error) {
      // Serialize error to string - never render objects directly
      const errorMessage = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.response?.data?.message || error.message || 'Onbekende fout';
      
      setTestResult({
        success: false,
        message: error.response 
          ? `Fout: ${error.response.status} - ${errorMessage}`
          : `Verbindingsfout: ${errorMessage}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestDatabase = async () => {
    setDbTesting(true);
    setDbTestResult(null);

    try {
      const finalApiUrl = settings.apiUrl || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const testApi = axios.create({
        baseURL: finalApiUrl,
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const response = await testApi.get('/health/database');
      
      // Build message from checks - only strings
      let message = response.data.message || '';
      if (response.data.checks) {
        const checkMessages = Object.entries(response.data.checks)
          .map(([key, check]) => `${key}: ${check.passed ? '✓' : '✗'} ${check.message}`)
          .join(' | ');
        if (checkMessages) {
          message = checkMessages;
        }
      }
      
      setDbTestResult({
        success: response.data.success,
        message: message || (response.data.success ? 'Database connectie succesvol!' : 'Database connectie gefaald'),
        checks: response.data.checks || {}
      });
    } catch (error) {
      // Serialize error to string - never render objects directly
      const errorMessage = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.response?.data?.message || error.message || 'Database test gefaald';
      
      setDbTestResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setDbTesting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" component="h1">
          Instellingen
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* API Instellingen */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Backend API Instellingen
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Configureer de URL van je backend API. Voor Vercel deployment gebruik je: <code>/api</code>
            </Typography>
            
            <TextField
              fullWidth
              label="API URL"
              value={settings.apiUrl}
              onChange={handleChange('apiUrl')}
              placeholder="/api of http://localhost:3001/api"
              margin="normal"
              helperText="Voor Vercel: /api | Voor lokale development: http://localhost:3001/api"
            />

            <Box mt={2}>
              <Button
                variant="outlined"
                onClick={handleTestConnection}
                disabled={loading}
                sx={{ mr: 2 }}
              >
                {loading ? 'Testen...' : 'Test Verbinding'}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Opslaan
              </Button>
            </Box>

            {testResult && (
              <Alert 
                severity={testResult.success ? 'success' : 'error'} 
                sx={{ mt: 2 }}
              >
                <Typography variant="body2">
                  {testResult.message}
                </Typography>
              </Alert>
            )}

            {saveSuccess && (
              <Alert severity="success" sx={{ mt: 2 }} icon={<CheckIcon />}>
                Instellingen opgeslagen!
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Database Test */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <DatabaseIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Database Connectie Test
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" paragraph>
              Test of de app verbonden is met de Supabase database. Controleer of SUPABASE_URL en SUPABASE_ANON_KEY zijn ingesteld in Vercel.
            </Typography>
            
            <Button
              variant="outlined"
              startIcon={dbTesting ? <RefreshIcon /> : <DatabaseIcon />}
              onClick={handleTestDatabase}
              disabled={dbTesting}
              sx={{ mb: 2 }}
            >
              {dbTesting ? 'Database testen...' : 'Test Database Connectie'}
            </Button>

            {dbTestResult && (
              <Alert 
                severity={dbTestResult.success ? 'success' : 'error'} 
                sx={{ mt: 2 }}
              >
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  {dbTestResult.message}
                </Typography>
                {dbTestResult.checks && Object.keys(dbTestResult.checks).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(dbTestResult.checks).map(([key, check]) => (
                      <Box key={key} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        {check.passed ? (
                          <CheckIcon color="success" sx={{ mr: 1, fontSize: '1rem' }} />
                        ) : (
                          <Typography color="error" sx={{ mr: 1 }}>✗</Typography>
                        )}
                        <Typography variant="body2">
                          <strong>{key}:</strong> {check.message}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* WooCommerce Instellingen */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              WooCommerce API Instellingen
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Deze instellingen worden gebruikt voor directe API calls vanuit de frontend (indien nodig).
              Voor Vercel deployment worden deze meestal ingesteld via environment variables op de backend.
            </Typography>

            <TextField
              fullWidth
              label="WooCommerce URL"
              value={settings.wooCommerceUrl}
              onChange={handleChange('wooCommerceUrl')}
              placeholder="https://jouwwinkel.nl"
              margin="normal"
              type="url"
            />

            <TextField
              fullWidth
              label="Consumer Key"
              value={settings.consumerKey}
              onChange={handleChange('consumerKey')}
              placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              margin="normal"
              type="password"
            />

            <TextField
              fullWidth
              label="Consumer Secret"
              value={settings.consumerSecret}
              onChange={handleChange('consumerSecret')}
              placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              margin="normal"
              type="password"
            />

            <Box mt={2}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Opslaan
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Info */}
        <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Typography variant="body2">
            <strong>Let op:</strong> Na het wijzigen van instellingen, herlaad de pagina om de wijzigingen volledig toe te passen.
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
};

export default Settings;
